import { FC, useEffect } from 'react';

import { Form, Select } from 'antd';
import dayjs from 'dayjs';
import { useParams } from 'react-router-dom';
import { Button, DatePicker, Input, Label, Typography } from 'ui';

import { EventScheduleReqType } from 'types/promoters/promoterTypes';

import s from './EventSchedule.module.scss';

interface ScheduleFormPropsType {
  initial?: EventScheduleReqType;
  onSubmit: (data: EventScheduleReqType) => void;
  onCancel: () => void;
}

const ScheduleForm: FC<ScheduleFormPropsType> = ({ initial, onSubmit, onCancel }) => {
  const [form] = Form.useForm();
  const { eventId } = useParams();

  useEffect(() => {
    if (initial) {
      form.setFieldsValue({
        name: initial.name,
        dateFrom: dayjs(initial.dateFrom),
        dateTo: dayjs(initial.dateTo),
      });
    }
  }, [initial, form]);

  const handleFinish = (values) => {
    const id = initial?.id ?? values.id;
    onSubmit({
      id,
      eventId: eventId ?? '',
      name: values.name,
      dateFrom: dayjs(values.dateFrom),
      dateTo: dayjs(values.dateTo),
      days: initial?.days ?? [],
      active: values.active,
    });
    form.resetFields();
  };

  return (
    <Form form={form} className={s.form} layout="inline" onFinish={handleFinish}>
      {initial ? (
        <Typography type="h4">Edit &ldquo;{initial.name}&ldquo; schedule to this event</Typography>
      ) : (
        <Typography type="h4">Add a schedule to this event</Typography>
      )}
      <p>
        Add a schedule to this event and then start by specifying a date range when the schedule
        will be effective
      </p>

      <div>
        <Label>
          Type any friendly name which make it easier for you to distinguish between schedules, for
          example ‘Winter schedule’
        </Label>

        <Form.Item
          validateFirst
          name="name"
          rules={[
            { required: true, message: 'Name is required' },
            { min: 5, message: 'Name must be at least 5 characters' },
          ]}>
          <Input autoComplete="off" name="name" placeholder="Friendly Name" />
        </Form.Item>
      </div>

      <div className={s.dateContainter}>
        <div>
          <Label>Date from (ex: 19-May-2025) when schedule is applicable</Label>
          <Form.Item name="dateFrom" rules={[{ required: true, message: 'Date from is required' }]}>
            <DatePicker fullWidth format="dddd DD MMMM YYYY" />
          </Form.Item>
        </div>
        <div>
          <Label>Date to (ex: 21-May-2025) when schedule is applicable</Label>
          <Form.Item name="dateTo" rules={[{ required: true, message: 'Date to is required' }]}>
            <DatePicker fullWidth format="dddd DD MMMM YYYY" />
          </Form.Item>
        </div>
      </div>
      <div style={{ marginTop: 8 }}>
        <Label>Active</Label>
        <Form.Item name="active" initialValue={true}>
          <Select
            style={{ width: '100%', height: 42 }}
            options={[
              { value: true, label: 'Yes' },
              { value: false, label: 'No' },
            ]}
          />
        </Form.Item>
      </div>
      <div className={s.formActions}>
        <Button className={s.formActionButton} type="primary" htmlType="submit">
          {initial ? 'EDIT' : 'ADD'} SCHEDULE
        </Button>
        <Button className={s.formActionButton} onClick={onCancel} type="link" htmlType="button">
          CANCEL
        </Button>
      </div>
    </Form>
  );
};

export default ScheduleForm;
